import { useState } from 'react';
import { Badge, Body1Strong, Button } from '@fluentui/react-components';
import { AddRegular, DeleteRegular, EditRegular } from '@fluentui/react-icons';

import { Bubble, ConfirmDialog, DateLabel, TabContent, TConfirmDialogState } from '@shared/components';

import { TLiteratureAssessment, TLiteratureResolvedItems } from '../../types';
import { EXCLUSION_REASON_ENABLED } from '../../constants';
import { useArchiveAssessment } from '../../api';
import {
  getDecisionBadgeColor,
  getDecisionLabel,
  getExclusionReasonLabel,
  getReviewingStageLabel,
} from '../../helpers';
import FieldCell from '../FieldCell';

type TProps = {
  literatureId: string;
  assessments: TLiteratureAssessment[];
  resolvedItems: TLiteratureResolvedItems;
  canEdit: boolean;
  hasDraft: boolean;
  onAdd: () => void;
  onEdit: (assessment: TLiteratureAssessment) => void;
};

const AssessmentsTab = ({ literatureId, assessments, resolvedItems, canEdit, hasDraft, onAdd, onEdit }: TProps) => {
  const archive = useArchiveAssessment();
  const [confirmState, setConfirmState] = useState<TConfirmDialogState | undefined>(undefined);
  const activeAssessments = assessments.filter((a) => !a.archived);

  const handleArchive = (assessment: TLiteratureAssessment) => {
    const productNames = assessment.products.map((p) => resolvedItems[p]?.name ?? p).join(', ');
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: 'Archive assessment?',
      content: `This will remove the assessment for ${productNames} and update citing documents.`,
      buttonLabel: 'Archive',
      danger: true,
      onConfirm: async () => {
        await archive.mutateAsync({ literatureId, assessmentId: assessment._id });
        setConfirmState({ ...dialogState, isOpen: false });
      },
    };
    setConfirmState(dialogState);
  };

  // No-draft state already shows Add assessment on the Overview panel - hide the tab duplicate to avoid two competing primaries.
  const header =
    canEdit && hasDraft ? (
      <div>
        <Button appearance="primary" icon={<AddRegular />} onClick={onAdd}>
          Add assessment
        </Button>
      </div>
    ) : null;

  return (
    <TabContent className="space-y-3">
      {header}
      {activeAssessments.length === 0 && <p className="text-neutral-500">No assessments yet.</p>}
      {activeAssessments.map((assessment) => {
        const productNames = assessment.products.map((p) => resolvedItems[p]?.name ?? p);
        const reviewerName = resolvedItems[assessment.reviewer]?.name ?? assessment.reviewer;
        return (
          <Bubble key={assessment._id}>
            <div className="flex flex-col space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Body1Strong>{productNames.join(', ')}</Body1Strong>
                  <Badge appearance="tint" color={getDecisionBadgeColor(assessment.decision)}>
                    {getDecisionLabel(assessment.decision)}
                  </Badge>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button
                      appearance="transparent"
                      icon={<EditRegular />}
                      aria-label="Edit assessment"
                      className="!p-0"
                      onClick={() => onEdit(assessment)}
                    />
                    <Button
                      appearance="transparent"
                      icon={<DeleteRegular />}
                      aria-label="Archive assessment"
                      className="!p-0"
                      onClick={() => handleArchive(assessment)}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-flow-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                <FieldCell label="Reviewer">{reviewerName}</FieldCell>
                <FieldCell label="Reviewed">
                  <DateLabel short>{assessment.reviewDate}</DateLabel>
                </FieldCell>
                {assessment.reviewingStage && (
                  <FieldCell label="Reviewing stage">{getReviewingStageLabel(assessment.reviewingStage)}</FieldCell>
                )}
                {EXCLUSION_REASON_ENABLED && assessment.exclusionReason && (
                  <FieldCell label="Exclusion reason">{getExclusionReasonLabel(assessment.exclusionReason)}</FieldCell>
                )}
                {assessment.comment && (
                  <FieldCell label="Comment" spanClass="md:col-span-2 lg:col-span-3">
                    <span className="whitespace-pre-wrap">{assessment.comment}</span>
                  </FieldCell>
                )}
              </div>
            </div>
          </Bubble>
        );
      })}
      <ConfirmDialog state={confirmState} setState={setConfirmState} />
    </TabContent>
  );
};

export default AssessmentsTab;
